import './MembershipTypeBasicsSection.css';

import { Check } from 'lucide-react';
import { MEMBERSHIP_THEME_TOKENS, normalizeMembershipThemeToken, resolveThemeToken } from '../themeTokens';

type MembershipTypeBasicsSectionProps = {
  values: {
    name: string;
    description: string;
    tierLevel: number;
    themeColorToken: string;
  };
  onChange: (key: string, value: string | number | boolean) => void;
  errors: Record<string, string>;
};

const TIER_OPTIONS = [
  { value: 1, title: '1', subtitle: 'Entry', description: 'Good for entry loyalty' },
  { value: 2, title: '2', subtitle: 'Mid', description: 'Balanced recurring tier' },
  { value: 3, title: '3', subtitle: 'Top', description: 'High-value regulars' },
  { value: 4, title: '4', subtitle: 'Elite', description: 'Top-tier members' },
] as const;

const MembershipTypeBasicsSection = ({ values, onChange, errors }: MembershipTypeBasicsSectionProps) => {
  const selectedThemeToken = normalizeMembershipThemeToken(values.themeColorToken);
  const selectedTheme = resolveThemeToken(values.themeColorToken);

  return (
    <section className="appointments-v2-card membership-basics-card">
      <div className="appointments-v2-card-header membership-basics-card__header">
        <div>
          <h3>Basics</h3>
          <p className="membership-basics-card__intro">Shape the identity customers will recognize on every membership card.</p>
        </div>
      </div>
      <div className="appointments-v2-card-body membership-basics-card__body">
      <label className="field membership-basics-card__field">
        <span className="label membership-basics-card__label">Membership Name</span>
        <input
          className="input membership-basics-card__input"
          value={values.name}
          placeholder="e.g. Silver Member, Gold Club..."
          maxLength={80}
          onChange={(e) => onChange('name', e.target.value)}
        />
        <div className="membership-basics-card__field-meta">
          <p className="membership-basics-card__helper">
            Pick a short, recognizable tier name customers will immediately understand.
          </p>
          <span className="membership-basics-card__char-count">{values.name.length}/80</span>
        </div>
        {errors.name ? <span className="membership-basics-card__error">{errors.name}</span> : null}
      </label>

      <label className="field membership-basics-card__field">
        <span className="label membership-basics-card__label">Description</span>
        <textarea
          className="input membership-basics-card__input membership-basics-card__textarea"
          rows={3}
          value={values.description}
          placeholder="What do members get? Keep it short and compelling..."
          maxLength={240}
          onChange={(e) => onChange('description', e.target.value)}
        />
        <div className="membership-basics-card__field-meta">
          <p className="membership-basics-card__helper">
            Appears on the membership card. Keep it to one or two benefit-led lines.
          </p>
          <span className="membership-basics-card__char-count">{values.description.length}/240</span>
        </div>
        {errors.description ? <span className="membership-basics-card__error">{errors.description}</span> : null}
      </label>

      <div className="membership-basics-card__grid">
        <div className="membership-basics-card__group">
          <span className="label membership-basics-card__label">Tier Level</span>
          <div className="membership-basics-card__tierRow">
            {TIER_OPTIONS.map((tier) => {
              const isActive = values.tierLevel === tier.value;
              return (
                <button
                  key={tier.value}
                  type="button"
                  className={`membership-basics-card__tierChip${isActive ? ' is-active' : ''}`}
                  onClick={() => onChange('tierLevel', tier.value)}
                  aria-pressed={isActive}
                >
                  {isActive ? <span className="membership-basics-card__tierCheck"><Check size={12} /></span> : null}
                  <span className="membership-basics-card__tierValue">{tier.title}</span>
                  <span className="membership-basics-card__tierText">{tier.subtitle}</span>
                  <span className="membership-basics-card__tierMeta">{tier.description}</span>
                </button>
              );
            })}
          </div>
          <p className="membership-basics-card__helper">
            These chips stay merchant-friendly while still persisting the numeric `tierLevel` underneath.
          </p>
          {errors.tierLevel ? <span className="membership-basics-card__error">{errors.tierLevel}</span> : null}
        </div>

        <div className="membership-basics-card__group">
          <span className="label membership-basics-card__label">Card Theme</span>
          <div className="membership-basics-card__swatchGrid">
            {MEMBERSHIP_THEME_TOKENS.map((themeToken) => {
              const theme = resolveThemeToken(themeToken);
              const isActive = selectedThemeToken === themeToken;
              return (
                <button
                  key={themeToken}
                  type="button"
                  className={`membership-basics-card__swatch${isActive ? ' is-active' : ''}`}
                  style={{ background: theme.cardBackground }}
                  onClick={() => onChange('themeColorToken', themeToken)}
                  aria-pressed={isActive}
                  aria-label={`Select ${theme.label} theme`}
                >
                  <span className="membership-basics-card__swatchInner" style={{ backgroundColor: theme.swatchHex }} />
                  {isActive ? <span className="membership-basics-card__swatchCheck"><Check size={12} /></span> : null}
                  <span className="membership-basics-card__swatchLabel">{theme.label}</span>
                </button>
              );
            })}
          </div>
          <p className="membership-basics-card__helper">
            Selected theme: <strong>{selectedTheme.label}</strong>
            {selectedThemeToken === 'standard' ? <span className="membership-basics-card__suggested"> (Default)</span> : null}
          </p>
          {errors.themeColorToken ? <span className="membership-basics-card__error">{errors.themeColorToken}</span> : null}
        </div>
      </div>
      </div>
    </section>
  );
};

export default MembershipTypeBasicsSection;
